import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { FilePen, Search } from 'lucide-react';
import { PiGavel } from 'react-icons/pi';

// Document positions (top-left corner for greyed/selected images)
const DOC_POSITIONS = [
  { x: 32, y: 40 },
  { x: 416, y: 40 },
  { x: 800, y: 40 },
  { x: 1184, y: 40 },
];

// Folder positions (top-left corner for greyed/selected images)
const FOLDER_POSITIONS = [
  { x: 120, y: 576 },
  { x: 576, y: 576 },
  { x: 1032, y: 576 },
];

// Folder data with icons and labels
const FOLDER_DATA = [
  { icon: FilePen, label: 'Motion' },
  { icon: Search, label: 'Discovery' },
  { icon: PiGavel, label: 'Trial' }
];

// Folder SVG path (from selected_folder.svg)
const FOLDER_PATH = "M290 274C298.487 274 306.626 270.629 312.627 264.627C318.629 258.626 322 250.487 322 242V82C322 73.5131 318.629 65.3737 312.627 59.3726C306.626 53.3714 298.487 50 290 50H163.6C158.248 50.0525 152.969 48.7618 148.245 46.2461C143.521 43.7304 139.503 40.07 136.56 35.6L123.6 16.4C120.686 11.9755 116.72 8.34366 112.056 5.83034C107.392 3.31702 102.178 2.00087 96.88 2H34C25.5131 2 17.3737 5.37142 11.3726 11.3726C5.37142 17.3737 2 25.5131 2 34V242C2 250.487 5.37142 258.626 11.3726 264.627C17.3737 270.629 25.5131 274 34 274H290Z";

// SVG line file positions (top-left within the 1408x888 frame)
const LINE_SVG_POSITIONS = [
  { x: 126, y: 260 },   // doc_sort_1
  { x: 510, y: 260 },   // doc_sort_2
  { x: 248, y: 260 },   // doc_sort_3
  { x: 246, y: 260 },   // doc_sort_4
];

// Path definitions - with manually reversed paths where needed
const PATH_DATA = {
  0: [ // doc_sort_1
    { 
      // REVERSED: Was "M62 162H50C23.4903 162 2 140.51 2 114V0"
      d: "M2 0V114C2 140.51 23.4903 162 50 162H62",
      type: 'trunk',
      targetFolder: null,
      waitFor: []
    },
    { 
      d: "M62 162H74C100.51 162 122 183.49 122 210V360",
      type: 'branch',
      targetFolder: 0,
      waitFor: [0]
    },
    { 
      d: "M62 162H530C556.51 162 578 183.49 578 210V360",
      type: 'branch',
      targetFolder: 1,
      waitFor: [0]
    }
  ],
  1: [ // doc_sort_2 (no reversal needed)
    {
      d: "M2.00058 0L2.00017 114C2.00008 140.51 23.4904 162 50.0002 162H146C172.51 162 194 183.49 194 210L194.001 360",
      type: 'branch',
      targetFolder: 1,
      waitFor: []
    }
  ],
  2: [ // doc_sort_3
    {
      // Trunk from top of doc to junction point
      d: "M650 0V6C650 32.5097 628.51 54 602 54H506C479.49 54 458 75.4903 458 102L458 108",
      type: 'trunk',
      targetFolder: null,
      waitFor: []
    },
    {
      // Branch straight down to folder 1
      d: "M458 108V360",
      type: 'branch',
      targetFolder: 1,
      waitFor: [0]
    },
    {
      // Branch right to folder 2
      d: "M458 108V114C458 140.51 479.49 162 506 162H686L866 162C892.51 162 914 183.49 914 210V360",
      type: 'branch',
      targetFolder: 2,
      waitFor: [0]
    },
    {
      // Branch left to folder 0
      d: "M458 108V114C458 140.51 436.51 162 410 162L230 162L50 162C23.4903 162 2 183.49 2 210V360",
      type: 'branch',
      targetFolder: 0,
      waitFor: [0]
    }
  ],
  3: [ // doc_sort_4
    {
      // REVERSED: Was "M974 162H986C1012.51 162 1034 140.51 1034 114V0"
      d: "M1034 0V114C1034 140.51 1012.51 162 986 162H974",
      type: 'trunk',
      targetFolder: null,
      waitFor: []
    },
    {
      d: "M974 162H962C935.49 162 914 183.49 914 210V360",
      type: 'branch',
      targetFolder: 2,
      waitFor: [0]
    },
    {
      d: "M974 162H50C23.4903 162 2.00003 183.49 2.00003 210V360",
      type: 'branch',
      targetFolder: 0,
      waitFor: [0]
    }
  ]
};

// Animation timing
const LINE_SPEED = 800; // pixels per second for drawing
const LINE_RETRACT_SPEED = 1000; // pixels per second for retracting
const DOC_GROW_DURATION = 0.2;
const FOLDER_GROW_DURATION = 0.3; // Folder grow time
const FOLDER_OPEN_DURATION = 0.4; // Folder open time
const FOLDER_HOLD_DURATION = 0.8; // How long to hold open
const FOLDER_CLOSE_DURATION = 0.1; // Folder close time
const FOLDER_SHRINK_DURATION = 0.4; // Folder shrink time
const PAUSE_DURATION = 0.5;
const BETWEEN_DOCS_PAUSE = 0.7;

// Helper to calculate path length
const calculatePathLength = (pathElement) => {
  if (!pathElement) return 400; // fallback
  try {
    return pathElement.getTotalLength();
  } catch {
    return 400; // fallback
  }
};

const OrganizeAnimation = () => {
  const [activeDoc, setActiveDoc] = useState(null);
  const [animationPhase, setAnimationPhase] = useState('idle');
  const [activeFolders, setActiveFolders] = useState(new Set());
  const [folderPhases, setFolderPhases] = useState({}); // Track each folder's animation phase
  const pathRefs = useRef({});
  const folderAnimationRefs = useRef({}); // Track ongoing folder animations to prevent overlaps

  // Start animation cycle
  useEffect(() => {
    startCycle();
  }, []);

  const startCycle = async () => {
    for (let docIndex = 0; docIndex < 4; docIndex++) {
      await animateDoc(docIndex);
      await wait(BETWEEN_DOCS_PAUSE * 1000);
    }
    // Loop
    startCycle();
  };

  const animateDoc = async (docIndex) => {
    setActiveDoc(docIndex);
    setActiveFolders(new Set());
    setFolderPhases({});

    // Phase 1: Doc grows
    setAnimationPhase('docGrow');
    await wait(DOC_GROW_DURATION * 1000);

    // Phase 2: Lines draw (folders grow as each line reaches them)
    setAnimationPhase('linesDrawing');
    await animateAllPaths(docIndex, false); // forward

    // Phase 3: Pause
    setAnimationPhase('pause');
    await wait(PAUSE_DURATION * 1000);

    // Phase 4: Lines retract (doc shrinks simultaneously, folders shrink as retraction reaches them)
    setAnimationPhase('linesRetracting');
    await animateAllPaths(docIndex, true); // reverse

    setActiveDoc(null);
    setAnimationPhase('idle');
  };

  const animateAllPaths = async (docIndex, reverse) => {
    const paths = PATH_DATA[docIndex];
    
    // Calculate the total duration (including delays) for the entire animation
    let maxTotalDuration = 0;
    paths.forEach((pathData, pathIndex) => {
      const pathElement = pathRefs.current[`${docIndex}-${pathIndex}`];
      const pathDuration = reverse 
        ? calculatePathLength(pathElement) / LINE_RETRACT_SPEED 
        : calculatePathLength(pathElement) / LINE_SPEED;
      
      // Calculate delay by summing durations of all dependencies
      let delay = 0;
      if (pathData.waitFor && pathData.waitFor.length > 0) {
        pathData.waitFor.forEach(depIndex => {
          const depElement = pathRefs.current[`${docIndex}-${depIndex}`];
          const depDuration = reverse
            ? calculatePathLength(depElement) / LINE_RETRACT_SPEED
            : calculatePathLength(depElement) / LINE_SPEED;
          delay += depDuration;
        });
      }
      
      const totalDuration = delay + pathDuration;
      if (totalDuration > maxTotalDuration) {
        maxTotalDuration = totalDuration;
      }
      
      if (pathData.targetFolder !== null) {
        if (!reverse) {
          // During drawing: folder animation starts when branch FINISHES (delay + duration)
          setTimeout(async () => {
            setActiveFolders(prev => new Set([...prev, pathData.targetFolder]));
            await animateFolderSequence(pathData.targetFolder);
          }, totalDuration * 1000);
        } else {
          // During retraction: folder closing starts early so it finishes when retraction reaches it
          const closeStartTime = Math.max(0, (totalDuration - FOLDER_CLOSE_DURATION) * 1000);
          setTimeout(async () => {
            await animateFolderClose(pathData.targetFolder);
          }, closeStartTime);
        }
      }
    });

    // Wait for the longest total path animation (delay + duration) to complete
    await wait(maxTotalDuration * 1000);
  };

  // Animate folder opening sequence: grow -> open -> hold
  const animateFolderSequence = async (folderIndex) => {
    // Mark this folder as animating
    folderAnimationRefs.current[folderIndex] = 'opening';
    
    // Phase 1: Growing
    setFolderPhases(prev => ({ ...prev, [folderIndex]: 'growing' }));
    await wait(FOLDER_GROW_DURATION * 1000);
    
    // Check if animation was cancelled
    if (folderAnimationRefs.current[folderIndex] !== 'opening') return;
    
    // Phase 2: Opening
    setFolderPhases(prev => ({ ...prev, [folderIndex]: 'opening' }));
    await wait(FOLDER_OPEN_DURATION * 1000);
    
    // Check if animation was cancelled
    if (folderAnimationRefs.current[folderIndex] !== 'opening') return;
    
    // Phase 3: Open (hold)
    setFolderPhases(prev => ({ ...prev, [folderIndex]: 'open' }));
    await wait(FOLDER_HOLD_DURATION * 1000);
    
    // Animation complete (unless cancelled)
    if (folderAnimationRefs.current[folderIndex] === 'opening') {
      folderAnimationRefs.current[folderIndex] = 'idle';
    }
  };

  // Animate folder closing sequence: closing -> shrinking
  const animateFolderClose = async (folderIndex) => {
    // If folder is already closing or not animating, skip
    if (folderAnimationRefs.current[folderIndex] === 'closing' || 
        !folderAnimationRefs.current[folderIndex]) {
      return;
    }
    
    // Cancel any opening animation in progress
    folderAnimationRefs.current[folderIndex] = 'closing';
    
    // Phase 1: Closing
    setFolderPhases(prev => ({ ...prev, [folderIndex]: 'closing' }));
    await wait(FOLDER_CLOSE_DURATION * 1000);
    
    // Check if animation was cancelled
    if (folderAnimationRefs.current[folderIndex] !== 'closing') return;
    
    // Phase 2: Shrinking
    setFolderPhases(prev => ({ ...prev, [folderIndex]: 'shrinking' }));
    await wait(FOLDER_SHRINK_DURATION * 1000);
    
    // Remove from active folders
    setActiveFolders(prev => {
      const newSet = new Set(prev);
      newSet.delete(folderIndex);
      return newSet;
    });
    setFolderPhases(prev => {
      const newPhases = { ...prev };
      delete newPhases[folderIndex];
      return newPhases;
    });
    
    // Mark as idle
    folderAnimationRefs.current[folderIndex] = null;
  };

  const wait = (ms) => new Promise(resolve => setTimeout(resolve, ms));

  return (
    <div className="w-full h-full flex items-center justify-center">
      <svg
        viewBox="0 0 1408 888"
        className="w-full h-auto"
      >
        {/* Render documents */}
        {DOC_POSITIONS.map((pos, i) => {
          const isActive = activeDoc === i && 
            (animationPhase === 'docGrow' || animationPhase === 'linesDrawing' || animationPhase === 'pause');
          const scale = isActive ? 256 / 192 : 1;
          const centerX = pos.x + 96; // Original center
          const centerY = pos.y + 96;

          return (
            <motion.image
              key={`doc-${i}`}
              href={isActive ? '/doc.svg' : '/greyed_doc.svg'}
              width={192}
              height={192}
              x={pos.x}
              y={pos.y}
              animate={{ scale }}
              transition={{ duration: DOC_GROW_DURATION }}
              style={{
                transformOrigin: `${centerX}px ${centerY}px`,
                transformBox: 'fill-box'
              }}
            />
          );
        })}

        {/* Render line paths for active doc */}
        {activeDoc !== null && PATH_DATA[activeDoc] && (
          <g transform={`translate(${LINE_SVG_POSITIONS[activeDoc].x}, ${LINE_SVG_POSITIONS[activeDoc].y})`}>
            {PATH_DATA[activeDoc].map((pathData, pathIndex) => {
              const pathElement = pathRefs.current[`${activeDoc}-${pathIndex}`];
              const pathDuration = calculatePathLength(pathElement) / LINE_SPEED;
              const pathRetractDuration = calculatePathLength(pathElement) / LINE_RETRACT_SPEED;
              
              // Calculate delay by summing durations of all dependencies
              // Use retract speed if we're in retraction phase
              let delay = 0;
              if (pathData.waitFor && pathData.waitFor.length > 0) {
                pathData.waitFor.forEach(depIndex => {
                  const depElement = pathRefs.current[`${activeDoc}-${depIndex}`];
                  const depDuration = animationPhase === 'linesRetracting'
                    ? calculatePathLength(depElement) / LINE_RETRACT_SPEED
                    : calculatePathLength(depElement) / LINE_SPEED;
                  delay += depDuration;
                });
              }
              
              // Determine target pathLength, pathOffset, and opacity based on animation phase
              let pathLength = 0;
              let pathOffset = 0;
              let pathOpacity = 1;
              let duration = 0;
              let actualDelay = 0;
              
              if (animationPhase === 'linesDrawing') {
                // Drawing: animate from 0 to 1
                pathLength = 1;
                pathOffset = 0;
                pathOpacity = 1;
                duration = pathDuration;
                actualDelay = delay;
              } else if (animationPhase === 'pause') {
                // Stay fully drawn during pause
                pathLength = 1;
                pathOffset = 0;
                pathOpacity = 1;
                duration = 0;
                actualDelay = 0;
              } else if (animationPhase === 'linesRetracting') {
                // Retracting: keep length at 1, but offset from 0 to 1 (eraser effect)
                pathLength = 1;
                pathOffset = 1;
                pathOpacity = 1;
                duration = pathRetractDuration;
                actualDelay = delay;
              } else {
                // For docGrow and idle phases, hide the path
                pathLength = 0;
                pathOffset = 0;
                pathOpacity = 0;
                duration = 0;
                actualDelay = 0;
              }

              return (
                <motion.path
                  key={`path-${pathIndex}`}
                  ref={el => pathRefs.current[`${activeDoc}-${pathIndex}`] = el}
                  d={pathData.d}
                  stroke="#303840"
                  strokeWidth="4"
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  initial={{ pathLength: 0, pathOffset: 0, opacity: 0 }}
                  animate={{ pathLength, pathOffset, opacity: pathOpacity }}
                  transition={{
                    pathLength: {
                      duration: duration,
                      delay: actualDelay,
                      ease: "linear"
                    },
                    pathOffset: {
                      duration: duration,
                      delay: actualDelay,
                      ease: "linear"
                    },
                    opacity: {
                      duration: 0, // Instant transition
                      delay: actualDelay, // But still respect the delay
                      ease: "linear"
                    }
                  }}
                />
              );
            })}
          </g>
        )}

        {/* Render folders with opening animation */}
        {FOLDER_POSITIONS.map((pos, i) => {
          const isActive = activeFolders.has(i);
          const phase = folderPhases[i] || 'idle';
          const folderData = FOLDER_DATA[i];
          const IconComponent = folderData.icon;
          
          // Determine states
          const isGrowing = phase === 'growing';
          const isOpen = phase === 'opening' || phase === 'open' || phase === 'closing';
          const isClosing = phase === 'closing';
          const isShrinking = phase === 'shrinking';
          
          // Scale: grey=1, active=1.25 (320/256)
          const scale = (isGrowing || isOpen || isClosing || isShrinking) ? 1.25 : 1;
          
          // Skew angles
          const frontSkew = isOpen ? (isClosing ? 0 : -8) : 0;
          const backSkew = isOpen ? (isClosing ? 0 : 8) : 0;
          
          const centerX = pos.x + 128;
          const centerY = pos.y + 128;

          return (
            <g key={`folder-${i}`}>
              <foreignObject
                x={pos.x}
                y={pos.y}
                width={256}
                height={256}
                style={{
                  transformOrigin: `${centerX}px ${centerY}px`,
                  transformBox: 'fill-box',
                  overflow: 'visible'
                }}
              >
                <motion.div
                  style={{ width: '256px', height: '256px' }}
                  animate={{ scale }}
                  transition={{ duration: FOLDER_GROW_DURATION }}
                >
                  {/* Back layer of folder */}
                  <motion.div
                    className="absolute inset-0"
                    style={{
                      transformOrigin: '50% 100%'
                    }}
                    animate={{ skewX: backSkew }}
                    transition={{ 
                      duration: FOLDER_OPEN_DURATION, 
                      ease: phase === 'closing' ? "easeInOut" : [0.34, 1.56, 0.64, 1] 
                    }}
                  >
                    <svg
                      width="256"
                      height="256"
                      viewBox="0 0 324 276"
                      className="overflow-visible"
                    >
                      <path
                        d={FOLDER_PATH}
                        fill={isActive ? "#FBFFB3" : "#C0C7CE"}
                        stroke="#0C141D"
                        strokeWidth="4"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </motion.div>

                  {/* Front layer - folder, icon, and text grouped together */}
                  <motion.div
                    className="absolute inset-0"
                    style={{
                      transformOrigin: '50% 100%'
                    }}
                    animate={{ skewX: frontSkew }}
                    transition={{ 
                      duration: FOLDER_OPEN_DURATION, 
                      ease: phase === 'closing' ? "easeInOut" : [0.34, 1.56, 0.64, 1] 
                    }}
                  >
                    {/* Front folder SVG */}
                    <svg
                      width="256"
                      height="256"
                      viewBox="0 0 324 276"
                      className="overflow-visible"
                    >
                      <path
                        d={FOLDER_PATH}
                        fill={isActive ? "#F2FF00" : "#E1E5EA"}
                        stroke="#0C141D"
                        strokeWidth="4"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    
                    {/* Icon - centered on folder */}
                    <div
                      className="absolute"
                      style={{
                        top: '45%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)'
                      }}
                    >
                      <IconComponent 
                        size={72} 
                        strokeWidth={2}
                        color={isActive ? "#303840" : "#C0C7CE"}
                      />
                    </div>
                    
                    {/* Text label */}
                    <motion.div
                      className="absolute text-4xl font-semibold text-gray-800 whitespace-nowrap"
                      style={{
                        top: '65%',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        color: '#303840'
                      }}
                      initial={{ opacity: 0 }}
                      animate={{
                        opacity: (isGrowing || isOpen || isClosing) ? 1 : 0
                      }}
                      transition={{
                        opacity: { duration: 0.3 }
                      }}
                    >
                      {folderData.label}
                    </motion.div>
                  </motion.div>
                </motion.div>
              </foreignObject>
            </g>
          );
        })}
      </svg>
    </div>
  );
};

export default OrganizeAnimation;